// pages/Home.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import { 
  Trophy, Users, Shield, Coins, Zap, Clock, Star, 
  Gamepad2, Wallet, Menu, LogOut, User as UserIcon,
  Award, ChevronRight, Sparkles, Crown, Rocket,
} from 'lucide-react';

interface Tournament {
  id: string;
  title: string;
  game: string;
  entryFee: number;
  prizePool: number;
  registered: number;
  slots: number;
  date: string;
  time: string;
  status: 'UPCOMING' | 'ONGOING' | 'COMPLETED';
  image?: string;
  mod?: string;
}

const Home = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [selectedGame, setSelectedGame] = useState('all');
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [error, setError] = useState<string | null>(null); 
  const stats= {
    activePlayers: 2000,
    prizeDistributed: 25500,
    totalTournaments: 78,
    happyPlayers: 99
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Fetch Real Tournaments from Firebase
  useEffect(() => {
    const fetchTournaments = async () => {
      setLoadingTournaments(true);
      const db = getDatabase();
      const tournamentsList: Tournament[] = [];
      
      try {
        // Fetch BGMI tournaments
        const bgmiRef = ref(db, 'Tournaments/BGMI');
        const bgmiSnapshot = await get(bgmiRef);
        
        if (bgmiSnapshot.exists()) {
          const bgmiData = bgmiSnapshot.val();
          Object.entries(bgmiData).forEach(([id, data]: [string, any]) => {
            if (data.status === 'UPCOMING' || data.status === 'ONGOING') {
              tournamentsList.push({
                id,
                game: 'BGMI',
                title: data.title || 'BGMI Tournament',
                entryFee: data.joinOptions?.coinCost || 0,
                prizePool: data.prizePool || 0,
                registered: data.registered || 0,
                slots: data.slots || 100,
                date: data.date || '',
                time: data.time || '',
                status: data.status || 'UPCOMING',
                mod: data.mod || 'Squad'
              });
            }
          });
        }
        
        // Fetch FFMAX tournaments
        const ffRef = ref(db, 'Tournaments/FFMAX');
        const ffSnapshot = await get(ffRef);
        
        if (ffSnapshot.exists()) {
          const ffData = ffSnapshot.val();
          Object.entries(ffData).forEach(([id, data]: [string, any]) => {
            if (data.status === 'UPCOMING' || data.status === 'ONGOING') {
              tournamentsList.push({
                id,
                game: 'Free Fire Max',
                title: data.title || 'FFMAX Tournament',
                entryFee: data.joinOptions?.coinCost || 0,
                prizePool: data.prizePool || 0,
                registered: data.registered || 0,
                slots: data.slots || 100,
                date: data.date || '',
                time: data.time || '',
                status: data.status || 'UPCOMING',
                mod: data.mod || 'Squad'
              });
            }
          });
        }
        
        // Sort by date (earliest first)
        const parseDate = (dateStr: string, timeStr: string) => {
          if (!dateStr || !timeStr) return Date.now();
          const [day, month, year] = dateStr.split('/');
          const [time, modifier] = timeStr.split(' ');
          let [hours, minutes] = time.split(':').map(Number);
          if (modifier === 'PM' && hours < 12) hours += 12;
          if (modifier === 'AM' && hours === 12) hours = 0;
          return new Date(Number(year), Number(month) - 1, Number(day), hours, minutes).getTime();
        };
        
        tournamentsList.sort((a, b) => parseDate(a.date, a.time) - parseDate(b.date, b.time));
        
        setTournaments(tournamentsList);
        
      } catch (error) {
        console.error('Error fetching tournaments:', error);
        setError('Unable to load tournaments. Please try again later.');
      } finally {
        setLoadingTournaments(false);
      }
    };
    
    fetchTournaments();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setShowProfileMenu(false);
    navigate('/');
  };

  const formatINR = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTimeRemaining = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return 'Soon';
    const [day, month, year] = dateStr.split('/');
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (modifier === 'PM' && hours < 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    const matchTime = new Date(Number(year), Number(month) - 1, Number(day), hours, minutes).getTime();
    const now = Date.now();
    const diff = matchTime - now;
    
    if (diff <= 0) return 'Started';
    
    const hoursLeft = Math.floor(diff / (1000 * 60 * 60));
    const daysLeft = Math.floor(hoursLeft / 24);
    
    if (daysLeft > 0) return `${daysLeft}d ${hoursLeft % 24}h left`;
    if (hoursLeft > 0) return `${hoursLeft}h left`;
    return `${Math.floor(diff / (1000 * 60))}m left`;
  };

  const features = [
    { icon: Trophy, title: 'Live Tournaments', desc: 'Join & win cash prizes daily', color: 'from-yellow-500 to-orange-500' },
    { icon: Shield, title: 'Secure Platform', desc: '100% safe & verified games', color: 'from-blue-500 to-cyan-500' },
    { icon: Coins, title: 'Instant Payouts', desc: 'Direct withdrawal to UPI', color: 'from-green-500 to-emerald-500' },
    { icon: Users, title: '24/7 Support', desc: 'Always here to help you', color: 'from-purple-500 to-pink-500' },
  ];

  const statsData = [
    { value: stats.activePlayers.toLocaleString(), label: 'Active Players', icon: Users, suffix: '+' },
    { value: formatINR(stats.prizeDistributed), label: 'Prize Distributed', icon: Trophy, suffix: '' },
    { value: stats.totalTournaments.toString(), label: 'Tournaments', icon: Gamepad2, suffix: '+' },
    { value: `${stats.happyPlayers}%`, label: 'Happy Players', icon: Star, suffix: '' },
  ];

  const filteredTournaments = tournaments.filter(t => 
    selectedGame === 'all' || t.game === selectedGame
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#0f0f1a] to-[#0a0a0f]">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-3xl animate-pulse-slow" />
      </div>

      {/* Navbar */}
      <nav className="fixed top-0 w-full bg-gradient-to-r from-[#0a0a0f]/95 via-[#0f0f1a]/95 to-[#0a0a0f]/95 backdrop-blur-xl border-b border-purple-500/20 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-rajdhani font-bold text-xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                ARENA X
              </span>
            </motion.div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              {['Tournaments', 'Leaderboard', 'FAQ', 'About'].map((item) => (
                <motion.button
                  key={item}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => item === 'Tournaments' ? navigate('/dashboard') : navigate(`/${item.toLowerCase()}`)}
                  className="text-gray-300 hover:text-purple-400 transition font-rajdhani font-bold"
                >
                  {item}
                </motion.button>
              ))}
            </div>

            {/* Auth Buttons / User Menu */}
            <div className="flex items-center gap-3">
              {user ? (
                <div className="relative">
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowProfileMenu(!showProfileMenu)} 
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 px-4 py-2 rounded-xl border border-purple-500/30 hover:border-purple-500/50 transition-all"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{user.displayName?.[0] || user.email?.[0]?.toUpperCase() || 'U'}</span>
                    </div>
                    <span className="text-white hidden sm:inline font-rajdhani">{user.displayName || user.email?.split('@')[0]}</span>
                  </motion.button>
                  
                  <AnimatePresence>
                    {showProfileMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-48 bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0f] rounded-xl border border-purple-500/30 overflow-hidden shadow-xl"
                      >
                        {[
                          { icon: UserIcon, label: 'Dashboard', path: '/dashboard', color: 'text-purple-400' },
                          { icon: Wallet, label: 'Wallet', path: '/dashboard/wallet', color: 'text-yellow-500' },
                        ].map((item) => (
                          <button
                            key={item.label}
                            onClick={() => {
                              navigate(item.path);
                              setShowProfileMenu(false);
                            }}
                            className="w-full px-4 py-2 text-left text-gray-300 hover:bg-white/10 flex items-center gap-2 transition"
                          >
                            <item.icon className={`w-4 h-4 ${item.color}`} />
                            <span className="font-rajdhani">{item.label}</span>
                          </button>
                        ))}
                        <div className="h-px bg-white/10 my-1" />
                        <button onClick={handleLogout} className="w-full px-4 py-2 text-left text-red-400 hover:bg-white/10 flex items-center gap-2 transition">
                          <LogOut className="w-4 h-4" /> <span className="font-rajdhani">Logout</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/login')} 
                    className="px-4 py-2 text-purple-400 border border-purple-500 rounded-xl hover:bg-purple-500/10 transition font-rajdhani font-bold"
                  >
                    Login
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/signup')} 
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl text-white hover:shadow-lg hover:shadow-purple-500/25 transition font-rajdhani font-bold"
                  >
                    Sign Up
                  </motion.button>
                </>
              )}
              
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
                className="md:hidden text-white"
              >
                <Menu className="w-6 h-6" />
              </motion.button>
            </div>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden mt-4 pt-4 border-t border-purple-500/20"
              >
                <div className="flex flex-col gap-3">
                  {['Tournaments', 'Leaderboard', 'FAQ', 'About'].map((item) => (
                    <button
                      key={item}
                      onClick={() => {
                        navigate(item === 'Tournaments' ? '/dashboard' : `/${item.toLowerCase()}`);
                        setIsMobileMenuOpen(false);
                      }}
                      className="text-gray-300 hover:text-purple-400 py-2 font-rajdhani font-bold text-left"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 font-rajdhani font-bold text-sm">INDIA'S #1 GAMING PLATFORM</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-rajdhani font-bold mb-6">
                <span className="text-white">Play. Win.</span>
                <br />
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                  Withdraw Instantly
                </span>
              </h1>
              <p className="text-xl text-gray-400 font-rajdhani mb-8">
                Join thousands of players in BGMI & Free Fire tournaments. Win real cash and withdraw directly to your bank account.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => user ? navigate('/dashboard') : navigate('/signup')}
                  className="bg-gradient-to-r from-purple-500 to-blue-500 px-8 py-4 rounded-xl text-white font-rajdhani font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-purple-500/25 transition-all group"
                >
                  <Rocket className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  {user ? 'Go to Dashboard' : 'Start Winning Now'}
                </motion.button>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="flex items-center gap-1 text-green-400"><Shield className="w-4 h-4" /><span>100% Legal</span></div>
                <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                <div className="flex items-center gap-1 text-green-400"><Award className="w-4 h-4" /><span>Skill-Based</span></div>
                <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                <div className="flex items-center gap-1 text-green-400"><Clock className="w-4 h-4" /><span>24/7 Support</span></div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-1 rounded-3xl">
                <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-3xl p-6">
                  <div className="grid grid-cols-2 gap-4">
                    {features.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        whileHover={{ y: -5 }}
                        className="bg-white/5 p-4 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all"
                      >
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${feature.color} flex items-center justify-center mb-3`}>
                          <feature.icon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-white font-rajdhani font-bold text-sm">{feature.title}</h3>
                        <p className="text-gray-400 text-xs mt-1">{feature.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full p-3 animate-pulse shadow-lg">
                <Crown className="w-6 h-6 text-white" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {statsData.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] border border-purple-500/30 rounded-2xl p-6 text-center hover:border-purple-500/50 transition-all"
              >
                <stat.icon className="w-8 h-8 text-purple-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-gray-400 font-rajdhani">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tournaments Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <div>
              <h2 className="text-4xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Live Tournaments
              </h2>
              <p className="text-gray-400 mt-2">Join now and start winning real cash prizes</p>
            </div>
            <div className="flex gap-2">
              {['all', 'BGMI', 'Free Fire Max'].map((game) => (
                <motion.button
                  key={game}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedGame(game)}
                  className={`px-4 py-2 rounded-xl text-sm font-rajdhani font-bold transition-all ${
                    selectedGame === game 
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg shadow-purple-500/25' 
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  {game === 'all' ? 'All Games' : game}
                </motion.button>
              ))}
            </div>
          </div>

          {loadingTournaments ? (
            <div className="flex justify-center py-12">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Gamepad2 className="w-6 h-6 text-purple-400 animate-pulse" />
                </div>
              </div>
            </div>
          ) : filteredTournaments.length === 0 ? (
            <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-12 text-center border border-purple-500/30">
              <Gamepad2 className="w-16 h-16 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 font-rajdhani text-lg">No tournaments available</p>
              <p className="text-gray-500 text-sm mt-1">Check back soon for new tournaments!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredTournaments.slice(0, 8).map((tournament, index) => (
                <motion.div
                  key={tournament.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="group bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all duration-300 overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="text-4xl">
                        {tournament.game === 'BGMI' ? '🎮' : '🔥'}
                      </div>
                      <div className="bg-red-500/20 px-2 py-1 rounded-full">
                        <Clock className="w-3 h-3 text-red-400 inline mr-1" />
                        <span className="text-red-400 text-xs">
                          {getTimeRemaining(tournament.date, tournament.time)}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-white font-rajdhani font-bold text-lg mb-1 line-clamp-1">
                      {tournament.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">{tournament.game}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Entry Fee:</span>
                        <span className="text-purple-400 font-bold">
                          {tournament.entryFee > 0 ? formatINR(tournament.entryFee) : 'FREE'}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Prize Pool:</span>
                        <span className="text-yellow-500 font-bold">{formatINR(tournament.prizePool)}</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-full h-2 transition-all duration-500"
                          style={{ width: `${(tournament.registered / tournament.slots) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>{tournament.registered}/{tournament.slots} joined</span>
                        <span className="text-purple-400">
                          {Math.floor((tournament.registered / tournament.slots) * 100)}% filled
                        </span>
                      </div>
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => user ? navigate(`/tournament/${tournament.game === 'BGMI' ? 'BGMI' : 'FFMAX'}/${tournament.id}`) : navigate('/login')}
                      className="w-full bg-gradient-to-r from-purple-500 to-blue-500 py-2.5 rounded-xl text-white font-rajdhani font-bold text-sm hover:shadow-lg hover:shadow-purple-500/25 transition-all relative overflow-hidden group"
                    >
                      <span className="relative z-10">
                        {user ? 'Join Tournament' : 'Login to Join'}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          
          {filteredTournaments.length > 8 && (
            <div className="text-center mt-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 font-rajdhani font-bold"
              >
                View All Tournaments <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block p-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl mb-6 shadow-lg shadow-purple-500/25">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold mb-4">
              <span className="text-white">Ready to </span>
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Compete</span>
              <span className="text-white">?</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani mb-8">
              Join ArenaX now and start winning real cash prizes
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => user ? navigate('/dashboard') : navigate('/signup')}
                className="bg-gradient-to-r from-purple-500 to-blue-500 px-12 py-5 rounded-xl text-white font-rajdhani font-bold text-lg flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-purple-500/25 transition-all group"
              >
                <Zap className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                {user ? 'Go to Dashboard' : 'Create Free Account'}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-t from-[#0a0a0f] to-transparent border-t border-purple-500/20 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-rajdhani font-bold text-xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  ARENA X
                </span>
              </div>
              <p className="text-gray-400 text-sm">India's premier skill-based gaming platform.</p>
            </div>
            
            <div>
              <h4 className="text-white font-rajdhani font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/about')} className="text-gray-400 hover:text-purple-400 text-sm transition">About Us</button></li>
                <li><button onClick={() => navigate('/faq')} className="text-gray-400 hover:text-purple-400 text-sm transition">FAQ</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-rajdhani font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/terms')} className="text-gray-400 hover:text-purple-400 text-sm transition">Terms & Conditions</button></li>
                <li><button onClick={() => navigate('/privacy')} className="text-gray-400 hover:text-purple-400 text-sm transition">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/refund')} className="text-gray-400 hover:text-purple-400 text-sm transition">Refund Policy</button></li>
                <li><button onClick={() => navigate('/fairplay')} className="text-gray-400 hover:text-purple-400 text-sm transition">Fair Play Policy</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-rajdhani font-bold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/guidelines')} className="text-gray-400 hover:text-purple-400 text-sm transition">Community Guidelines</button></li>
                <li><p className="text-gray-400 text-sm">Email: support@arenax.com</p></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-purple-500/20 pt-8 text-center">
            <p className="text-gray-500 text-sm">© 2024 ArenaX. All rights reserved. 18+ only. Skill-based gaming platform.</p>
            <p className="text-gray-500 text-xs mt-2">This game involves an element of financial risk and may be addictive. Please play responsibly.</p>
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap');
        
        .font-rajdhani {
          font-family: 'Rajdhani', sans-serif;
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Home;